import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import BarChart01Icon from "@redotech/redo-icons/bar-chart-01.svg";
import BarChart01FilledIcon from "@redotech/redo-icons/bar-chart-01_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardFilledIcon from "@redotech/redo-icons/dashboard_filled.svg";
import DatabaseIcon from "@redotech/redo-icons/database-02.svg";
import DatabaseFilledIcon from "@redotech/redo-icons/database-02_filled.svg";
import FileCheck02Icon from "@redotech/redo-icons/file-check-02.svg";
import FileCheck02FilledIcon from "@redotech/redo-icons/file-check-02_filled.svg";
import Globe02Icon from "@redotech/redo-icons/globe-02.svg";
import Globe02FilledIcon from "@redotech/redo-icons/globe-02_filled.svg";
import LayoutGrid01Icon from "@redotech/redo-icons/layout-grid-01.svg";
import LayoutGrid01FilledIcon from "@redotech/redo-icons/layout-grid-01_filled.svg";
import Map01Icon from "@redotech/redo-icons/map-01.svg";
import Map01FilledIcon from "@redotech/redo-icons/map-01_filled.svg";
import Route01Icon from "@redotech/redo-icons/route.svg";
import Route01FilledIcon from "@redotech/redo-icons/route_filled.svg";
import Settings02Icon from "@redotech/redo-icons/settings-02.svg";
import Settings02FilledIcon from "@redotech/redo-icons/settings-02_filled.svg";
import Tag01Icon from "@redotech/redo-icons/tag-01.svg";
import Tag01FilledIcon from "@redotech/redo-icons/tag-01_filled.svg";
import Tool01Icon from "@redotech/redo-icons/tool-01.svg";
import Tool01FilledIcon from "@redotech/redo-icons/tool-01_filled.svg";
import Truck01Icon from "@redotech/redo-icons/truck-01.svg";
import Truck01FilledIcon from "@redotech/redo-icons/truck-01_filled.svg";
import ZapIcon from "@redotech/redo-icons/zap.svg";
import ZapFilledIcon from "@redotech/redo-icons/zap_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useCapability } from "@redotech/redo-merchant-app-common/use-capability";
import {
  SettingItem,
  SettingsSection,
  SettingsSectionEnum,
} from "@redotech/redo-web/arbiter-components/navigation/redo-settings-nav";
import { filterTruthy } from "@redotech/util/array";
import {
  getOmsSettingsTabLabel,
  omsSettingsTabs,
} from "../../../setting/order-management/order-management-settings-tabs";
import {
  canViewScanningSettings,
  isRestrictedShippingTeam,
} from "../shipping-settings-access";

export function useFulfillmentSettings(): SettingsSection | undefined {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const omsEnabled = useCapability("oms");

  const canManageFulfillmentSettings = useHasPermission(
    SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
  );

  const canManageAdminGeneralSettings = useHasPermission(
    SettingsPermissions.SET_GENERAL_SETTINGS,
  );
  const canManageShippingSettings = useHasPermission(
    SettingsPermissions.SET_SHIPPING_SETTINGS,
  );

  if (
    !(omsEnabled && canManageFulfillmentSettings) &&
    !canManageAdminGeneralSettings &&
    !canManageShippingSettings
  ) {
    return undefined;
  }

  const omsFulfill = canViewScanningSettings(
    omsEnabled,
    canManageFulfillmentSettings,
  );

  const items: SettingItem[] = filterTruthy([
    (omsFulfill ||
      (canManageShippingSettings && !isRestrictedShippingTeam(team._id))) && {
      id: "order-management",
      href: routes.getSettingsSubRoute("order-management"),
      friendlyName: t("Order management"),
      searchTabs: omsSettingsTabs.map((tab) => ({
        id: tab,
        label: getOmsSettingsTabLabel(tab),
      })),
      searchTerms: [
        "order preparation",
        "order intake",
        "addresses",
        "address validation",
        "address exceptions and outreach",
        "order eligibility",
        "pickup and carrier handoff",
        "carrier handoff",
        "USPS manifest",
        "protected fulfillment actions",
        "order lookup",
        "parcel verification photos",
        "fulfillment defaults",
        "fulfillment locations",
        "international shipping",
        "international shipments",
        "custom fulfillment data",
        "merge suggestions",
        "auto-merge criteria",
        "merged shipment outcomes",
        "merge exceptions",
        "bulk shipment suggestions",
        "automated learning",
        "auto-split orders",
        "auto-split conditions",
        "when to split",
        "how to split",
        "after splitting",
        "split exceptions",
        "product sku mappings",
        "fulfillment promise",
        "fulfillment cutoff",
        "fulfillment schedule",
        "use a custom schedule",
        "working days and cutoffs",
        "timezone",
        "print profiles",
        "documents",
        "pick list",
        "printers and workstations",
        "fulfillment actions",
        "default action button",
        "bulk shipment matching",
        "one click print",
        "presets and shortcuts",
        "shipping presets",
        "keyboard shortcuts",
        "hotkeys",
        "packages",
        "parcels",
        "package dimensions",
        "package weight",
        "custom imports",
        "CSV column mappings",
        "import templates",
      ],
      UnfilledIcon: Settings02Icon,
      FilledIcon: Settings02FilledIcon,
    },
    canManageAdminGeneralSettings && {
      id: "order-editing-button",
      href: routes.getSettingsSubRoute("order-management/order-editing"),
      friendlyName: t("Order editing"),
      searchTerms: ["order holds", "editing rules"],
      UnfilledIcon: Tool01Icon,
      FilledIcon: Tool01FilledIcon,
    },
    omsFulfill && {
      id: "automations-oms",
      href: routes.getSettingsSubRoute("oms/automations"),
      friendlyName: t("Automations"),
      UnfilledIcon: ZapIcon,
      FilledIcon: ZapFilledIcon,
    },
    omsFulfill && {
      id: "packing-slips",
      href: routes.getSettingsSubRoute("oms/printing/packing-slips"),
      friendlyName: t("Packing slips"),
      UnfilledIcon: FileCheck02Icon,
      FilledIcon: FileCheck02FilledIcon,
    },
    omsFulfill && {
      id: "reports",
      href: routes.getSettingsSubRoute("oms/reports"),
      friendlyName: t("Reports"),
      UnfilledIcon: BarChart01Icon,
      FilledIcon: BarChart01FilledIcon,
    },
    omsFulfill && {
      id: "scanning-warehouse",
      href: routes.getSettingsSubRoute("oms/scanning-warehouse"),
      friendlyName: t("Scanning & warehouse"),
      UnfilledIcon: DatabaseIcon,
      FilledIcon: DatabaseFilledIcon,
    },
    omsFulfill && {
      id: "warehouse-dashboard",
      href: routes.getSettingsSubRoute("oms/warehouse-dashboard"),
      friendlyName: t("Warehouse dashboard"),
      UnfilledIcon: DashboardIcon,
      FilledIcon: DashboardFilledIcon,
    },
    omsFulfill && {
      id: "warehouse-layouts",
      href: routes.getSettingsSubRoute("oms/warehouse-layouts"),
      friendlyName: t("Warehouse layouts"),
      UnfilledIcon: LayoutGrid01Icon,
      FilledIcon: LayoutGrid01FilledIcon,
    },
    omsFulfill && {
      id: "work-order-flows",
      href: routes.getSettingsSubRoute("oms/work-order-flows"),
      friendlyName: t("Work order flows"),
      UnfilledIcon: Route01Icon,
      FilledIcon: Route01FilledIcon,
    },
  ]);

  return { name: SettingsSectionEnum.ORDER_FULFILLMENT, items };
}

export function withNewGeneralSearchTerms(
  section: SettingsSection | undefined,
): SettingsSection | undefined {
  if (!section) {
    return section;
  }
  const terms: Record<string, string[]> = {
    "billing-activity-button": [
      "billing activity",
      "billing reports",
      "transactions",
    ],
    "bills-button": [
      "bill summary",
      "billing summary",
      "bill totals",
      "invoices",
      "date range",
      "payment methods",
    ],
  };
  return {
    ...section,
    items: section.items.map((item) =>
      item.children
        ? {
            ...item,
            children: item.children.map((child) =>
              terms[child.id]
                ? { ...child, searchTerms: terms[child.id] }
                : child,
            ),
          }
        : item,
    ),
  };
}

export function useShippingSettings(): SettingsSection | undefined {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const team = useRequiredContext(TeamContext);
  const omsEnabled = useCapability("oms");

  const canManageShippingSettings = useHasPermission(
    SettingsPermissions.SET_SHIPPING_SETTINGS,
  );
  const canManageFulfillmentSettings = useHasPermission(
    SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
  );

  if (
    !canManageShippingSettings &&
    !(omsEnabled && canManageFulfillmentSettings)
  ) {
    return undefined;
  }

  const hideShippingSettings = isRestrictedShippingTeam(team._id);

  const items: SettingItem[] = [];

  const lcgEnabled = !!team?.settings?.landedCost?.enabled;

  if (canManageShippingSettings && !hideShippingSettings) {
    items.push({
      id: "carriers-link",
      href: routes.getSettingsSubRoute("carriers"),
      friendlyName: t("Carriers"),
      UnfilledIcon: Truck01Icon,
      FilledIcon: Truck01FilledIcon,
    });
    items.push({
      id: "freight-link",
      href: routes.getSettingsSubRoute("freight"),
      friendlyName: t("Freight"),
      UnfilledIcon: Map01Icon,
      FilledIcon: Map01FilledIcon,
    });
  }

  if (canManageShippingSettings && !hideShippingSettings && lcgEnabled) {
    items.push({
      id: "shipping-discounts-link",
      href: routes.getSettingsSubRoute("discounts"),
      friendlyName: t("Discounts"),
      UnfilledIcon: Tag01Icon,
      FilledIcon: Tag01FilledIcon,
    });
  }

  if (
    canManageShippingSettings &&
    !hideShippingSettings &&
    lcgEnabled &&
    !omsEnabled
  ) {
    items.push({
      id: "cross-border-link",
      href: routes.getSettingsSubRoute("cross-border"),
      friendlyName: t("Cross border"),
      UnfilledIcon: Globe02Icon,
      FilledIcon: Globe02FilledIcon,
    });
  }

  return items.length > 0
    ? { name: SettingsSectionEnum.SHIPPING, items }
    : undefined;
}
